'use client';

import React, { useState } from 'react';
import ShareIcon from '@heroicons/react/24/outline/ArrowUpOnSquareIcon';
import { FeedDto } from '@/api/feed';
import { useToast } from '@/context/toast-context';
import api from '@/api';
import styles from '@/screen/feed/view/components/like-button/styles.module.scss';

interface ShareButtonProps extends FeedDto {
  onShare?: () => void;
  className?: string;
  /** 아이콘 뒤에 함께 누를 수 있게 둘 내용(예: 공유 수). */
  children?: React.ReactNode;
}

/**
 * Component for share button
 */
const ShareButton: React.FC<ShareButtonProps> = ({ onShare, className = '', children, ...feed }: ShareButtonProps) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleShare = async () => {
    if (isLoading || !feed.sid) return;
    setIsLoading(true);

    try {
      const { linkId } = await api.shortLink.create('FEED', feed.sid);
      const shareUrl = `${window.location.origin}/l/${linkId}`;
      onShare?.();

      if (navigator.share) {
        await navigator.share({
          title: `LoNe에서 ${feed.owner?.nickname || feed.owner?.name}님이 올린 피드예요`,
          text: feed.caption,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.showToast('링크가 클립보드에 복사되었습니다.');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('공유하기 실패:', error);
        toast.showToast('링크 공유에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={`${styles.button} ${className}`}
      onClick={handleShare}
      aria-label="공유하기"
      aria-busy={isLoading}
    >
      <ShareIcon
        width={24}
        height={24}
        strokeWidth={1.5}
        aria-hidden="true"
      />
      {children}
    </button>
  );
};

export default ShareButton;
